import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

const ThunderBaySepticSystemsHub = () => {
  const title = "Septic Installers Thunder Bay | Groundhogs™ Septic Systems";
  const description =
    "Septic installers in Thunder Bay for engineered septic system installation and replacement. Serving Shuniah, Rosslyn, Gorham, Nipigon, Oliver Paipoonge, and Murillo. Traditional systems, Eljen GSF, holding tanks, and Roth MultiTank options.";

  const faq = [
    {
      question: "Do you install septic systems in Thunder Bay?",
      answer:
        "Yes. Groundhogs™ installs septic systems in Thunder Bay and surrounding Northwestern Ontario communities. Each system is designed for your soil, groundwater, bedrock, access, and Ontario Building Code requirements.",
    },
    {
      question: "What services do you provide (and what don’t you do)?",
      answer:
        "We specialize in septic system design, installation, and replacement. We do not offer septic tank pumping or cleaning.",
    },
    {
      question: "What septic options are common around Thunder Bay?",
      answer:
        "Depending on your site, the best option may be a traditional leaching bed, an Eljen GSF system for challenging soils, a raised bed/mound, or a holding tank. We assess the property and recommend the right design.",
    },
    {
      question: "Do you handle permits and inspections?",
      answer:
        "Yes. We coordinate permits/inspections and build systems to Ontario Building Code requirements and local authority processes.",
    },
    {
      question: "Can you replace just a septic tank?",
      answer:
        "Sometimes. If the drain field is still performing and compliant, a tank-only replacement may be possible. We confirm this with a site inspection.",
    },
  ];

  return (
    <div>
      <SEO
        title={title}
        description={description}
        keywords="septic installers Thunder Bay, septic installation Thunder Bay, septic system installation Thunder Bay, septic tank installation Thunder Bay, septic system replacement Thunder Bay"
        canonicalPath="/thunder-bay/septic-systems"
        structuredData={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Service",
              name: "Septic System Installation & Replacement in Thunder Bay",
              description,
              areaServed: {
                "@type": "AdministrativeArea",
                name: "Thunder Bay & Northwestern Ontario",
              },
              provider: {
                "@type": "LocalBusiness",
                name: "Groundhogs™ Septic Systems",
                url: "https://groundhogsseptics.com/",
              },
              serviceType: [
                "Septic system installation",
                "Septic system replacement",
                "Septic tank installation",
                "Septic tank replacement",
                "Eljen GSF installation",
                "Holding tank installation",
              ],
            },
            {
              "@type": "FAQPage",
              mainEntity: faq.map((item) => ({
                "@type": "Question",
                name: item.question,
                acceptedAnswer: {
                  "@type": "Answer",
                  text: item.answer,
                },
              })),
            },
          ],
        }}
      />

      <Header />

      <main className="pt-32 pb-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="mb-6 text-sm" aria-label="Breadcrumb">
            <ol className="flex flex-wrap items-center gap-1 text-gray-500">
              <li>
                <Link to="/" className="hover:underline text-orange-600">
                  Home
                </Link>
              </li>
              <li className="mx-1">/</li>
              <li className="text-gray-700">Thunder Bay</li>
              <li className="mx-1">/</li>
              <li className="text-gray-700">Septic Systems</li>
            </ol>
          </nav>

          <header className="gh-article-heading mb-10" data-reveal>
            <h1
              className="text-4xl sm:text-5xl font-bold mb-4"
              style={{ color: "var(--dark-navy)" }}
            >
              Septic Installers in Thunder Bay (Installation & Replacement)
            </h1>
            <p className="text-lg" style={{ color: "var(--text-light)" }}>
              Groundhogs™ provides engineered septic system installation and replacement for Thunder Bay and
              Northwestern Ontario properties. We work across Shuniah, Rosslyn, Gorham, Nipigon, Oliver Paipoonge, and
              Murillo and build systems designed for clay soils, shallow bedrock, high water tables, and Ontario
              Building Code compliance.
            </p>
            <p className="mt-3 text-sm" style={{ color: "var(--text-light)" }}>
              Note: we do not offer septic tank pumping or cleaning.
            </p>
          </header>

          <section className="mb-12">
            <div className="grid gap-6 md:grid-cols-3">
              <div className="p-5 rounded-lg" style={{ backgroundColor: "var(--light-gray)" }}>
                <h2 className="font-semibold mb-2" style={{ color: "var(--dark-navy)" }}>
                  1,200+ Northern Ontario Installs
                </h2>
                <p className="text-sm" style={{ color: "var(--text-light)" }}>
                  Proven installs and replacements built for Northwestern Ontario conditions.
                </p>
              </div>
              <div className="p-5 rounded-lg" style={{ backgroundColor: "var(--light-gray)" }}>
                <h2 className="font-semibold mb-2" style={{ color: "var(--dark-navy)" }}>
                  15+ Years Experience
                </h2>
                <p className="text-sm" style={{ color: "var(--text-light)" }}>
                  Site-specific designs for clay, bedrock, high groundwater, and tight lots.
                </p>
              </div>
              <div className="p-5 rounded-lg" style={{ backgroundColor: "var(--light-gray)" }}>
                <h2 className="font-semibold mb-2" style={{ color: "var(--dark-navy)" }}>
                  Licensed &amp; Insured
                </h2>
                <p className="text-sm" style={{ color: "var(--text-light)" }}>
                  OBC-focused planning, permits/inspections, and professional installation.
                </p>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-4" style={{ color: "var(--dark-navy)" }}>
              Services (Thunder Bay)
            </h2>
            <div className="grid gap-4 md:grid-cols-2">
              <Link
                to="/services/septic-system-installation-thunder-bay"
                className="p-5 rounded-lg border hover:bg-gray-50"
              >
                <div className="font-semibold" style={{ color: "var(--dark-navy)" }}>
                  Septic System Installation
                </div>
                <div className="text-sm text-gray-600">Traditional, Eljen, holding tank options.</div>
              </Link>
              <Link
                to="/services/septic-system-replacement-thunder-bay"
                className="p-5 rounded-lg border hover:bg-gray-50"
              >
                <div className="font-semibold" style={{ color: "var(--dark-navy)" }}>
                  Septic System Replacement
                </div>
                <div className="text-sm text-gray-600">Tank + field replacement and redesigns.</div>
              </Link>
              <Link
                to="/services/septic-tank-installation-thunder-bay"
                className="p-5 rounded-lg border hover:bg-gray-50"
              >
                <div className="font-semibold" style={{ color: "var(--dark-navy)" }}>
                  Septic Tank Installation &amp; Replacement
                </div>
                <div className="text-sm text-gray-600">Tank-only swaps when the field can be reused.</div>
              </Link>
              <Link
                to="/services/roth-multitank-supplier-thunder-bay"
                className="p-5 rounded-lg border hover:bg-gray-50"
              >
                <div className="font-semibold" style={{ color: "var(--dark-navy)" }}>
                  Roth MultiTank (Septic / Holding / Water)
                </div>
                <div className="text-sm text-gray-600">Supply-only or supply-and-install options.</div>
              </Link>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-4" style={{ color: "var(--dark-navy)" }}>
              Guides to Help You Plan
            </h2>
            <ul className="list-disc pl-6 space-y-2 text-orange-700">
              <li>
                <a href="/guides/septic-system-replacement-cost-thunder-bay" className="hover:underline">
                  Septic System Replacement Cost (Thunder Bay)
                </a>
              </li>
              <li>
                <a href="/guides/septic-system-cost-ontario" className="hover:underline">
                  Septic System Cost (Ontario)
                </a>
              </li>
              <li>
                <a href="/guides/septic-tank-installation-cost-ontario" className="hover:underline">
                  Septic Tank Installation Cost (Ontario)
                </a>
              </li>
              <li>
                <a href="/guides/percolation-test-thunder-bay-ontario" className="hover:underline">
                  Percolation Test (Thunder Bay / Ontario)
                </a>
              </li>
            </ul>
          </section>

          <section className="mt-12 p-6 rounded-lg" style={{ backgroundColor: "var(--light-gray)" }}>
            <h2 className="text-2xl font-bold mb-3" style={{ color: "var(--dark-navy)" }}>
              Get a Quote
            </h2>
            <p className="mb-5" style={{ color: "var(--text-light)" }}>
              The fastest way to get accurate pricing is a site assessment. We&apos;ll confirm soil constraints, access, and
              the best design for your property.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-6 py-3 rounded-md font-semibold text-white hover:opacity-90"
                style={{ backgroundColor: "var(--primary-orange)" }}
              >
                Request a Quote
              </Link>
              <Link
                to="/septic-calculator"
                className="inline-flex items-center justify-center px-6 py-3 rounded-md font-semibold border hover:bg-white"
                style={{ borderColor: "var(--primary-orange)", color: "var(--dark-navy)" }}
              >
                Try the Cost Calculator
              </Link>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ThunderBaySepticSystemsHub;
